import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, errorText, formatGeld } from '../api';
import { EinsatzPanel } from '../components/Einsatz';
import { KundeKasten, PositionenEditor, VorgangAktionen, VorgangKopf, VorgangVerlauf, leerePosition, positionenFuerApi, type PositionEingabe, type Schritt } from '../components/Vorgang';
import { Badge, Card, Dialog, Field, Loading, Notice } from '../components/ui';
import type { Serviceauftrag, Vorgang } from '../types';

const OFFENE_STATUS = [
  { wert: 'angenommen', label: 'Angenommen' },
  { wert: 'diagnose', label: 'In Diagnose' },
  { wert: 'in_reparatur', label: 'In Reparatur' },
  { wert: 'abholbereit', label: 'Abholbereit' },
];

const fertig = (ziel = 'abholbereit', extra: Partial<Schritt> = {}): Schritt => ({
  label: 'Fertig – abholbereit',
  ziel,
  notizPflicht: true,
  notizLabel: 'Ergebnis (repariert, nicht reparabel …)',
  ...extra,
});

function schritteFuer(s: Serviceauftrag, teile: () => void): Schritt[] {
  const teileSchritt: Schritt = { label: 'Teile bestellen', aktion: teile };
  if (s.art === 'aussendienst') {
    if (s.status === 'termin_offen') return [teileSchritt];
    if (s.status === 'teile_bestellt') return [{ label: 'Teile da – Termin vereinbaren', ziel: 'termin_offen', primary: true }];
    return [];
  }
  switch (s.status) {
    case 'angenommen':
      return [{ label: 'Diagnose beginnen', ziel: 'diagnose', primary: true }, { label: 'Direkt reparieren', ziel: 'in_reparatur' }, fertig()];
    case 'diagnose':
      return [
        { label: 'KVA an Kunden', ziel: 'kva_beim_kunden', primary: true, felder: ['diagnose', 'kva'], hinweis: 'Den Kunden anrufen oder anschreiben – die Wiedervorlage erinnert ans Nachhaken.' },
        { label: 'Reparieren', ziel: 'in_reparatur', felder: ['diagnose'] },
        teileSchritt,
        fertig('abholbereit', { label: 'Nicht reparabel – zurück' }),
      ];
    case 'kva_beim_kunden':
      return [
        { label: 'KVA angenommen – reparieren', ziel: 'in_reparatur', primary: true, kvaEntscheidung: 'angenommen' },
        { ...teileSchritt, label: 'KVA angenommen – Teile bestellen' },
        fertig('abholbereit', { label: 'KVA abgelehnt – zurück', kvaEntscheidung: 'abgelehnt' }),
      ];
    case 'teile_bestellt':
      return [{ label: 'Teile da – reparieren', ziel: 'in_reparatur', primary: true }, fertig('abholbereit', { label: 'Abbrechen – zurück an Kunden' })];
    case 'in_reparatur':
      return [fertig('abholbereit', { primary: true }), teileSchritt];
    case 'abholbereit':
      return [{ label: 'Kunde informiert', ziel: 'kunde_informiert', primary: true, notizLabel: 'Wie / wann holt er ab' }, { label: 'Abgeholt – abschließen', abschluss: true }];
    case 'kunde_informiert':
      return [{ label: 'Abgeholt – abschließen', abschluss: true, primary: true }];
    default:
      return [];
  }
}

export function ServiceDetailPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [s, setS] = useState<Serviceauftrag | null>(null);
  const [fehler, setFehler] = useState('');
  const [teile, setTeile] = useState(false);
  const [positionen, setPositionen] = useState<PositionEingabe[]>([leerePosition()]);
  const [lieferant, setLieferant] = useState('');
  const [bestellt, setBestellt] = useState(true);
  const [teileFehler, setTeileFehler] = useState('');

  const laden = useCallback(() => {
    api<Serviceauftrag>(`/api/service/${id}`)
      .then(setS)
      .catch((e) => setFehler(errorText(e)));
  }, [id]);
  useEffect(laden, [laden]);

  if (!s) return fehler ? <Notice kind="error">{fehler}</Notice> : <Loading />;

  async function teileBestellen() {
    setTeileFehler('');
    try {
      const r = await api<{ service: Serviceauftrag }>(`/api/service/${s!.id}/teile-bestellen`, {
        method: 'POST',
        body: { positionen: positionenFuerApi(positionen), lieferant: lieferant || null, bereitsBestellt: bestellt && !!lieferant.trim() },
      });
      setS(r.service);
      setTeile(false);
      setPositionen([leerePosition()]);
    } catch (e) {
      setTeileFehler(errorText(e));
    }
  }

  const titel = [s.geraeteart, s.marke, s.modell].filter(Boolean).join(' ');

  return (
    <>
      <VorgangKopf
        art="service"
        v={s}
        titel={titel}
        rechts={
          s.art === 'werkstatt' ? (
            <button className="klein" onClick={() => navigate(`/service/${s.id}/beleg`)}>
              🖨 Annahmebeleg
            </button>
          ) : (
            <span className="badge">Außendienst</span>
          )
        }
      />
      {params.get('neu') && (
        <Notice kind="success">
          Gerät angenommen. <Link to={`/service/${s.id}/beleg`}>Annahmebeleg jetzt drucken</Link>
        </Notice>
      )}
      {fehler && <Notice kind="error">{fehler}</Notice>}
      <div className="spalten haupt-links">
        <div className="stack">
          {s.art === 'aussendienst' && (s.einsaetze ?? []).length + (s.status === 'termin_offen' ? 1 : 0) > 0 && (
            <EinsatzPanel einsaetze={s.einsaetze ?? []} planbar={s.status === 'termin_offen'} ziel={{ serviceauftragId: s.id }} onNeu={laden} />
          )}
          <Card title="Was zu tun ist">
            <VorgangAktionen
              art="service"
              v={s}
              schritte={schritteFuer(s, () => setTeile(true))}
              onNeu={(v: Vorgang) => setS(v as Serviceauftrag)}
              offeneStatus={OFFENE_STATUS}
            />
          </Card>
          <Card title="Gerät und Fehler">
            <dl className="daten">
              <dt>Gerät</dt>
              <dd>{titel}</dd>
              <dt>Seriennr.</dt>
              <dd className="mono">{s.seriennummer ?? '—'}</dd>
              <dt>Zubehör</dt>
              <dd>{s.zubehoer ?? '—'}</dd>
              <dt>Fehler</dt>
              <dd style={{ whiteSpace: 'pre-wrap' }}>{s.fehlerbeschreibung}</dd>
              <dt>Diagnose</dt>
              <dd style={{ whiteSpace: 'pre-wrap' }}>{s.diagnose ?? '—'}</dd>
              {s.kva_cent != null && (
                <>
                  <dt>KVA</dt>
                  <dd>
                    {formatGeld(s.kva_cent)}{' '}
                    {s.kva_entscheidung ? <Badge kind={s.kva_entscheidung === 'angenommen' ? 'ok' : 'bad'}>{s.kva_entscheidung}</Badge> : <Badge kind="warn">offen</Badge>}
                  </dd>
                </>
              )}
              {s.wertgarantie && (
                <>
                  <dt>Wertgarantie</dt>
                  <dd className="mono">{s.wertgarantie_nr}</dd>
                </>
              )}
            </dl>
          </Card>
          {(s.bestellungen ?? []).length > 0 && (
            <Card title="Teilebestellungen" padded={false}>
              <table>
                <tbody>
                  {s.bestellungen!.map((b) => (
                    <tr key={b.id} className="klickbar" onClick={() => navigate(`/bestellungen/${b.id}`)}>
                      <td className="mono">{b.nummer}</td>
                      <td>{b.artikel}</td>
                      <td className="small muted">{b.lieferant}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Badge kind={b.status === 'abgeschlossen' ? 'ok' : b.status === 'eingetroffen' ? 'warn' : 'info'}>{b.status_bezeichnung ?? b.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
          <VorgangVerlauf art="service" v={s} onNeu={(v) => setS(v as Serviceauftrag)} />
        </div>

        <Card title="Angaben">
          <div className="stack" style={{ gap: 12 }}>
            <KundeKasten v={s} />
            <dl className="daten">
              <dt>Standort</dt>
              <dd>{s.standort ?? '—'}</dd>
              <dt>Techniker</dt>
              <dd>{s.zustaendig}</dd>
            </dl>
            {s.notiz && <div style={{ whiteSpace: 'pre-wrap' }} className="small">{s.notiz}</div>}
          </div>
        </Card>
      </div>

      {teile && (
        <Dialog
          titel="Teile bestellen"
          breit
          onClose={() => setTeile(false)}
          aktionen={
            <>
              <button onClick={() => setTeile(false)}>Abbrechen</button>
              <button className="primary" disabled={positionenFuerApi(positionen).length === 0} onClick={() => void teileBestellen()}>
                Bestellung anlegen
              </button>
            </>
          }
        >
          {teileFehler && <Notice kind="error">{teileFehler}</Notice>}
          <p className="small muted" style={{ margin: 0 }}>
            Legt eine Teilebestellung zu diesem Auftrag an. Sobald sie als eingetroffen gebucht wird, steht dieser Auftrag sofort wieder auf deiner Liste.
            {s.status === 'kva_beim_kunden' && ' Der KVA wird dabei als angenommen vermerkt.'}
          </p>
          <PositionenEditor positionen={positionen} onChange={setPositionen} mitPreis={false} />
          <div className="grid2">
            <Field label="Lieferant">
              <input value={lieferant} onChange={(e) => setLieferant(e.target.value)} placeholder="z. B. ASWO" />
            </Field>
            <label className="row" style={{ alignSelf: 'end' }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={bestellt} onChange={(e) => setBestellt(e.target.checked)} />
              Schon bestellt (sonst bestellt der Laden)
            </label>
          </div>
        </Dialog>
      )}
    </>
  );
}
